"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
interface SkeletonProps {
  className?: string;
}
export function DashboardSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-dashboard" aria-busy="true" aria-label="Loading dashboard" className={cn("animate-in fade-in space-y-6 p-6 duration-300", className)} data-sentry-component="DashboardSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {Array.from({
        length: 4
      }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Skeleton className="h-80 rounded-lg lg:col-span-2" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-80 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Skeleton className="h-64 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-64 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}
export function ChatSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-chat" aria-busy="true" aria-label="Loading chat" className={cn("animate-in fade-in flex h-full flex-col p-4 duration-200", className)} data-sentry-component="ChatSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center gap-3 border-b border-border pb-4">
        <Skeleton className="h-10 w-10 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <Skeleton className="h-3 w-20" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div className="flex-1 space-y-4 py-4">
        <div className="flex justify-start">
          <Skeleton className="h-16 w-3/4 rounded-2xl" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-12 w-1/2 rounded-2xl" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
        <div className="flex justify-start">
          <Skeleton className="h-24 w-2/3 rounded-2xl" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-10 w-1/3 rounded-2xl" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <Skeleton className="h-12 w-full rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}
export function StrategiesSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-strategies" aria-busy="true" aria-label="Loading strategies" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="StrategiesSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-36" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="flex gap-2">
        {Array.from({
        length: 4
      }).map((_, i) => <Skeleton key={i} className="h-9 w-24 rounded-full" />)}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="space-y-3 rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>)}
      </div>
    </div>;
}
export function ProfilesSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-profiles" aria-busy="true" aria-label="Loading profiles" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="ProfilesSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-44" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="space-y-4 rounded-lg border border-border p-5">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-16 rounded-md" />
              <Skeleton className="h-16 rounded-md" />
            </div>
            <Skeleton className="h-9 w-full" />
          </div>)}
      </div>
    </div>;
}
export function JournalSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-journal" aria-busy="true" aria-label="Loading journal" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="JournalSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <Skeleton className="h-10 w-10" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div className="flex gap-2">
        {Array.from({
        length: 5
      }).map((_, i) => <Skeleton key={i} className="h-9 w-20 rounded-md" />)}
      </div>

      <div className="space-y-3">
        {Array.from({
        length: 5
      }).map((_, i) => <div key={i} className="flex items-center gap-4 rounded-lg border border-border p-4">
            <Skeleton className="h-10 w-10 rounded-md" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-20" />
              </div>
              <Skeleton className="h-4 w-3/4" />
            </div>
            <Skeleton className="h-8 w-8" />
          </div>)}
      </div>
    </div>;
}
export function FlowSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-flow" aria-busy="true" aria-label="Loading flow" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="FlowSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-36" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-28" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
      </div>

      <Skeleton className="h-96 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton className="h-48 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-48 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}
export function NotificationsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-notifications" aria-busy="true" aria-label="Loading notifications" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="NotificationsSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-9 w-28" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="flex gap-2">
        <Skeleton className="h-9 w-16 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-9 w-20 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-9 w-24 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="space-y-2">
        {Array.from({
        length: 8
      }).map((_, i) => <div key={i} className="flex items-start gap-3 rounded-lg border border-border p-4">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-3 w-16" />
          </div>)}
      </div>
    </div>;
}
export function AccountSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-account" aria-busy="true" aria-label="Loading account" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="AccountSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center gap-4">
        <Skeleton className="h-20 w-20 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-4 rounded-lg border border-border p-5">
          <Skeleton className="h-5 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <div className="space-y-3">
            {Array.from({
            length: 4
          }).map((_, i) => <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>)}
          </div>
        </div>
        <div className="space-y-4 rounded-lg border border-border p-5">
          <Skeleton className="h-5 w-36" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <div className="space-y-3">
            {Array.from({
            length: 3
          }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
          </div>
        </div>
      </div>
    </div>;
}
export function SettingsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-settings" aria-busy="true" aria-label="Loading settings" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="SettingsSkeleton" data-sentry-source-file="index.tsx">
      <Skeleton className="h-8 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />

      <div className="flex gap-6">
        <div className="w-48 space-y-2">
          {Array.from({
          length: 6
        }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-md" />)}
        </div>

        <div className="flex-1 space-y-6 rounded-lg border border-border p-6">
          <Skeleton className="h-6 w-40" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <div className="space-y-4">
            {Array.from({
            length: 4
          }).map((_, i) => <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>)}
          </div>
          <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>
    </div>;
}
export function AlertsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-alerts" aria-busy="true" aria-label="Loading alerts" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="AlertsSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-28" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="flex gap-2">
        {Array.from({
        length: 4
      }).map((_, i) => <Skeleton key={i} className="h-9 w-20 rounded-full" />)}
      </div>

      <div className="space-y-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="flex items-center gap-4 rounded-lg border border-border p-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-8 w-8" />
          </div>)}
      </div>
    </div>;
}
export function BrokersSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-brokers" aria-busy="true" aria-label="Loading brokers" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="BrokersSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-10 w-36" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="space-y-4 rounded-lg border border-border p-5">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
            <Skeleton className="h-9 w-full" />
          </div>)}
      </div>
    </div>;
}
export function AdminSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-admin" aria-busy="true" aria-label="Loading admin dashboard" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="AdminSkeleton" data-sentry-source-file="index.tsx">
      <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {Array.from({
        length: 4
      }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Skeleton className="h-80 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-80 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <Skeleton className="h-64 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
    </div>;
}
export function TableSkeleton({
  rows = 5,
  className
}: SkeletonProps & {
  rows?: number;
}) {
  return <div data-testid="skeleton-table" aria-busy="true" aria-label="Loading table data" className={cn("animate-in fade-in space-y-2 duration-200", className)} data-sentry-component="TableSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex gap-4 border-b border-border pb-3">
        <Skeleton className="h-4 w-8" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-4 w-20" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-4 w-28" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-4 w-16" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      {Array.from({
      length: rows
    }).map((_, i) => <div key={i} className="flex gap-4 py-3">
          <Skeleton className="h-4 w-8" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-16" />
        </div>)}
    </div>;
}
export function CardSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-card" aria-busy="true" aria-label="Loading card" className={cn("animate-in fade-in space-y-3 rounded-lg border border-border bg-card p-4 duration-200", className)} data-sentry-component="CardSkeleton" data-sentry-source-file="index.tsx">
      <Skeleton className="h-5 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      <Skeleton className="h-4 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      <Skeleton className="h-4 w-3/4" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
    </div>;
}
export function PageSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-page" aria-busy="true" aria-label="Loading page" className={cn("animate-in fade-in space-y-4 p-6 duration-200", className)} data-sentry-component="PageSkeleton" data-sentry-source-file="index.tsx">
      <Skeleton className="h-8 w-64" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      <Skeleton className="h-[calc(100vh-200px)] rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
    </div>;
}
export function SigmaDecisionsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-sigma-decisions" aria-busy="true" aria-label="Loading Sigma decisions" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="SigmaDecisionsSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
          <Skeleton className="h-10 w-10" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {Array.from({
        length: 4
      }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
      </div>

      <div className="space-y-3">
        {Array.from({
        length: 5
      }).map((_, i) => <div key={i} className="flex items-center gap-4 rounded-lg border border-border p-4">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-4 w-3/4" />
            </div>
            <div className="text-right space-y-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>)}
      </div>
    </div>;
}
export function RewardsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-rewards" aria-busy="true" aria-label="Loading rewards" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="RewardsSkeleton" data-sentry-source-file="index.tsx">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        <Skeleton className="h-6 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {Array.from({
        length: 3
      }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
      </div>

      <Skeleton className="h-6 w-40" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
        {Array.from({
        length: 12
      }).map((_, i) => <div key={i} className="flex flex-col items-center space-y-2 rounded-lg border border-border p-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>)}
      </div>
    </div>;
}
export function ToolsSkeleton({
  className
}: SkeletonProps) {
  return <div data-testid="skeleton-tools" aria-busy="true" aria-label="Loading tools" className={cn("animate-in fade-in space-y-6 p-6 duration-200", className)} data-sentry-component="ToolsSkeleton" data-sentry-source-file="index.tsx">
      <Skeleton className="h-8 w-48" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} className="space-y-3 rounded-lg border border-border p-5">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>)}
      </div>
    </div>;
}